import { useRef, useState, useEffect } from 'react';
import { featuredArtists } from '@/mocks/soniem';

export default function FeaturedArtists() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      setCanScrollLeft(el.scrollLeft > 10);
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = 320;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="px-4 md:px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 md:mb-14">
          <div className="max-w-2xl">
            <span className="inline-block text-xs md:text-sm font-semibold uppercase tracking-widest text-primary-600 mb-3">
              Nuestros Artistas
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
              Representamos a los mejores talentos{' '}
              <span className="text-primary-500">del Perú</span>
            </h2>
          </div>
          <p className="text-foreground-600 text-sm md:text-base max-w-md">
            Más de 3,300 artistas nacionales y extranjeros confían en SONIEM 
            para la gestión y protección de sus derechos musicales.
          </p>
        </div>

        {/* Scrollable Cards Container */}
        <div className="relative group">
          {/* Left Arrow */}
          <button
            onClick={() => scroll('left')}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
              canScrollLeft
                ? 'bg-foreground-900 text-white opacity-100 cursor-pointer hover:bg-foreground-800'
                : 'bg-foreground-900/50 text-white/50 opacity-0 cursor-default'
            } md:opacity-0 md:group-hover:opacity-100 -translate-x-2 md:group-hover:translate-x-0`}
            aria-label="Desplazar artistas a la izquierda"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>

          {/* Cards */}
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 md:px-6 lg:px-10 md:-mx-6 lg:-mx-10"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {featuredArtists.map((artist) => (
              <div
                key={artist.id}
                className="flex-shrink-0 w-[260px] md:w-[300px] scroll-snap-align-start"
                style={{ scrollSnapAlign: 'start' }}
              >
                <div className="group/card relative rounded-2xl overflow-hidden bg-background-100 transition-all duration-500 hover:-translate-y-2">
                  {/* Image */}
                  <div className="relative w-full h-[340px] md:h-[400px] overflow-hidden">
                    <img
                      src={artist.image}
                      alt={artist.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover/card:scale-105"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    {/* Genre badge */}
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/15 backdrop-blur-sm text-white border border-white/20 whitespace-nowrap">
                        {artist.genre}
                      </span>
                    </div>
                    {/* Name & Role */}
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-1">
                        {artist.name}
                      </h3>
                      <p className="text-white/70 text-xs md:text-sm">
                        {artist.role}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          <button
            onClick={() => scroll('right')}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
              canScrollRight
                ? 'bg-foreground-900 text-white opacity-100 cursor-pointer hover:bg-foreground-800'
                : 'bg-foreground-900/50 text-white/50 opacity-0 cursor-default'
            } md:opacity-0 md:group-hover:opacity-100 translate-x-2 md:group-hover:translate-x-0`}
            aria-label="Desplazar artistas a la derecha"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}